import { Guid } from "guid-typescript";
import { CostType, FundingSourceType } from "./common";
import { FormGroup } from "@angular/forms";

export interface BudgetItem {
  id: Guid;
  type: CostType;
  description: string;
  amountRequested: number;
  totalAmount: number;
  explanation: string;
  fileStorageLocation: string;
}

export interface FundingSource {
  id: Guid;
  type: FundingSourceType;
  amount: number;
}


export class BudgetPlan {
  id: string;
  budgetItems: BudgetItem[];
  fundingSources: FundingSource[];

  constructor() {
    this.id = Guid.EMPTY.toString();
    this.budgetItems = [];
    this.fundingSources = [];
  }

  get totalDirectCostsRequested(): number {
    return this.budgetItems
      .filter((item) => item.type === CostType.Direct)
      .reduce((acc, item) => acc + (item.amountRequested || 0), 0);
  }

  get totalDirectCostsActual(): number {
    return this.budgetItems
      .filter((item) => item.type === CostType.Direct)
      .reduce((acc, item) => acc + (item.totalAmount || 0), 0);
  }

  get totalIndirectCostsRequested(): number {
    return this.budgetItems
      .filter((item) => item.type === CostType.Indirect)
      .reduce((acc, item) => acc + (item.amountRequested || 0), 0);
  }

  get totalIndirectCostsActual(): number {
    return this.budgetItems
      .filter((item) => item.type === CostType.Indirect)
      .reduce((acc, item) => acc + (item.totalAmount || 0), 0);
  }

  get totalCostsRequested(): number {
    return this.totalDirectCostsRequested + this.totalIndirectCostsRequested;
  }

  get totalCostsActual(): number {
    return this.totalDirectCostsActual + this.totalIndirectCostsActual;
  }

  get totalFundingFromOtherSources(): number {
    return this.fundingSources.reduce(
      (acc, source) => acc + (source.amount || 0),
      0
    );
  }

  get totalFunding(): number {
    return this.totalCostsRequested + this.totalFundingFromOtherSources;
  }

  static fromFormGroup(budgetFormGroup: FormGroup): BudgetPlan {
    const budgetPlan = new BudgetPlan();
    const formValue = budgetFormGroup.value;
    
    budgetPlan.id = formValue.id || Guid.EMPTY.toString();
    
    const directCosts = (formValue.directCosts || []).map((item: any) => ({
      ...item,
      type: CostType.Direct
    }));
    
    const indirectCosts = (formValue.indirectCosts || []).map((item: any) => ({
      ...item,
      type: CostType.Indirect
    }));
    
    budgetPlan.budgetItems = [...directCosts, ...indirectCosts];
    
    budgetPlan.fundingSources = formValue.fundingSources || [];
    
    return budgetPlan;
  }

  public static fromJson(json: any): BudgetPlan {
    const plan = new BudgetPlan();

    plan.id = json.id || Guid.EMPTY.toString();

    if (Array.isArray(json.budgetItems)) {
      plan.budgetItems = json.budgetItems.map((item: any) => {
        return { ...item };
      });
    }

    if (Array.isArray(json.fundingSources)) {
      plan.fundingSources = json.fundingSources.map((item: any) => {
        return { ...item };
      });
    }

    return plan;
  }
}
